import React, { useState } from 'react';
import {
  Button, ButtonText, HStack, Icon, Text,
} from '@gluestack-ui/themed';
import { Linking } from 'react-native';
import { TriangleAlert, Info, X } from 'lucide-react-native';
import { SafeAreaView } from '../../components/SafeAreaView';
import log from '../../utils/logger';

const formatUrl = (url) => {
  if (!url) return null;
  return url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
};

const STYLES = {
  warning: {
    bg: '$statusWarningSubtle',
    iconColor: '$statusWarning',
    textColor: '$textOnWarningSubtle',
    borderColor: '$statusWarning',
    icon: TriangleAlert,
  },
  info: {
    bg: '$statusInfoSubtle',
    iconColor: '$statusInfo',
    textColor: '$textOnInfoSubtle',
    borderColor: '$statusInfo',
    icon: Info,
  },
};

const InfoBar = ({
  message,
  level = 'info',
  displayFrom,
  displayTo,
  buttonLabel,
  buttonIcon,
  buttonLink,
  onButtonPress,
  onDismiss,
  customBg,
  customIconColor,
  allowDismiss = true,
}) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const hasButton = !!((buttonLabel || buttonIcon) && (buttonLink || onButtonPress));

  if (!message || isDismissed) {
    return null;
  }

  // Check if message should be shown based on time window
  const now = new Date();
  const isInTimeWindow = level === 'warning' // Always show warnings
    || ((!displayFrom || new Date(displayFrom) <= now) && (!displayTo || new Date(displayTo) >= now));

  // Force level to be either 'info' or 'warning'
  const type = level === 'warning' ? 'warning' : 'info';
  const style = STYLES[type];

  const handlePress = () => {
    if (!buttonLink && !onButtonPress) return;
    if (onButtonPress) {
      onButtonPress();
    } else {
      try {
        Linking.openURL(formatUrl(buttonLink));
      } catch (error) {
        log.warn('Error opening URL:', error);
      }
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  if (!isInTimeWindow) {
    return null;
  }

  const canDismiss = allowDismiss && type !== 'warning';

  return (
    <SafeAreaView>
      <HStack
        bg={customBg || style.bg}
        borderBottomWidth={1}
        borderColor={customIconColor || style.borderColor}
        p="$2"
        px="$4"
        w="$full"
        justifyContent="center"
        alignItems="center"
      >
        <HStack space="md" alignItems="center" maxWidth="$full">
          <Icon as={style.icon} color={customIconColor || style.iconColor} size="sm" />
          <Text size="xs" color={style.textColor} numberOfLines={2}>
            {message}
          </Text>
          {hasButton ? (
            <Button
              action="secondary"
              variant="outline"
              size="xs"
              onPress={handlePress}
              px={buttonIcon && !buttonLabel ? '$2' : '$3.5'}
              borderColor={customIconColor || style.borderColor}
            >
              {buttonIcon && <Icon as={buttonIcon} color={style.textColor} size="xs" mr={buttonLabel ? '$1' : '$0'} />}
              {buttonLabel && <ButtonText color={style.textColor}>{buttonLabel}</ButtonText>}
            </Button>
          ) : canDismiss ? (
            <Button
              action="secondary"
              variant="outline"
              size="xs"
              onPress={handleDismiss}
              px="$2"
              borderColor={customIconColor || style.borderColor}
            >
              <Icon as={X} color={style.textColor} size="xs" />
            </Button>
          ) : null}
        </HStack>
      </HStack>
    </SafeAreaView>
  );
};

export default InfoBar;
